import { APP_BASE_URL, BASE_URL } from "@/constants/Configs";
import { getGlobalSessionId } from "@/services";
import useClassActionsStore from "@/store/classActions";
import useUserStore from "@/store/user";
import { showError } from "@/utils/toast";
import Constants from "expo-constants";
import { router } from "expo-router";
import { StyleSheet } from "@/utils/appStyleSheet";
import { WebView } from "react-native-webview";
import { useState, useEffect } from "react";
import { View, ActivityIndicator, StyleSheet as RNStyleSheet } from "react-native";
import type { WebViewErrorEvent, WebViewHttpErrorEvent } from "react-native-webview/lib/WebViewTypes";
import { Colors } from "@/constants/Colors";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as Sentry from "@sentry/react-native";

export default function SignScreen() {
  const sessionId = getGlobalSessionId();
  const { activeSuit } = useClassActionsStore();
  const { getUserProfile } = useUserStore();
  const [isLoading, setIsLoading] = useState(true);
  const { bottom } = useSafeAreaInsets();

  // Safety check - if no active suit, go back with error
  useEffect(() => {
    if (!activeSuit?.id) {
      Sentry.captureMessage("SignScreen: No active suit when screen mounted", {
        level: "error",
        extra: { activeSuit },
      });
      showError("Failed to load claim form. Please try again.");
      router.back();
    }
  }, [activeSuit]);

  const handleWebViewError = (syntheticEvent: WebViewErrorEvent) => {
    const { nativeEvent } = syntheticEvent;
    Sentry.captureMessage("SignScreen: WebView error", {
      level: "error",
      extra: { nativeEvent, suitId: activeSuit?.id },
    });
    showError("Failed to load claim form. Please try again.");
    router.back();
  };

  const handleHttpError = (syntheticEvent: WebViewHttpErrorEvent) => {
    const { nativeEvent } = syntheticEvent;
    if (nativeEvent.statusCode >= 400) {
      Sentry.captureMessage("SignScreen: WebView HTTP error", {
        level: "error",
        extra: { statusCode: nativeEvent.statusCode, suitId: activeSuit?.id },
      });
      showError("Failed to load claim form. Please try again.");
      router.back();
    }
  };

  const handleNavigation = (request: any) => {
    const url = request.url;
    if (url.includes("/post-claim-refer")) {
      // TODO: handle post claim refer
      getUserProfile();
      router.replace({
        pathname: "/configuration",
        params: {
          image: `${BASE_URL}/brands/${activeSuit.brandImage}`,
          title: `You claimed from ${activeSuit?.name}!`,
          description: `You’ll hear back from the claim administrator within 3-12 months.`,
        },
      });
    }
    if (url.includes("/file/select")) {
      router.back();
    }

    return true;
  };

  const handleMessage = (event: any) => {
    const data = JSON.parse(event.nativeEvent.data);
    const type = data.type;
    if (type === "back") {
      router.back();
      return;
    }
    const isSuccess = data.success === "true";
    const message = data.message;
    if (isSuccess) {
      router.replace({
        pathname: "/configuration",
        params: {
          image: `${BASE_URL}/brands/${activeSuit.brandImage}`,
          title: `You claimed from ${activeSuit?.name}!`,
          description: `You’ll hear back from the claim administrator within 3-12 months.`,
        },
      });
    } else {
      showError(message);
      router.back();
    }
  };

  // Show loading while checking/redirecting if no suit
  if (!activeSuit?.id) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <WebView
        style={styles.webview}
        source={{
          uri: `${APP_BASE_URL}/file?select=${activeSuit.id}&sessionId=${sessionId}&fromApp=true`,
        }}
        javaScriptEnabled={true}
        cacheEnabled={false}
        cacheMode="LOAD_NO_CACHE"
        incognito={true}
        onShouldStartLoadWithRequest={handleNavigation}
        onMessage={handleMessage}
        onLoadStart={() => setIsLoading(true)}
        onLoadEnd={() => setIsLoading(false)}
        onError={handleWebViewError}
        onHttpError={handleHttpError}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: Constants.statusBarHeight,
  },
  webview: {
    flex: 1,
  },
  loadingOverlay: {
    ...RNStyleSheet.absoluteFillObject,
    backgroundColor: Colors.background,
    justifyContent: "center",
    alignItems: "center",
  },
});
